import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { adminStats, adminListArticles, adminDeleteArticle, adminContactSubmissions, adminNewsletter, adminSchedulerStatus, adminSchedulerRunNow, adminSchedulerRunDaily } from "../../lib/api";
import MetaTags from "../../lib/MetaTags";
import { Plus, Sparkles, Trash2, Edit, LogOut, Mail, Bell, FileText, Star, Bot, Play, Clock } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [contact, setContact] = useState([]);
  const [subs, setSubs] = useState([]);
  const [sched, setSched] = useState(null);
  const [running, setRunning] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) navigate("/admin21");
  }, [user, authLoading, navigate]);

  const loadArticles = (search) => adminListArticles(search).then(setArticles).catch(() => {});
  const loadStats = () => adminStats().then(setStats).catch(() => {});

  useEffect(() => {
    if (!user) return;
    loadArticles();
    loadStats();
  }, [user]);

  useEffect(() => {
    if (tab === "contact") adminContactSubmissions().then(setContact).catch(() => {});
    if (tab === "newsletter") adminNewsletter().then(setSubs).catch(() => {});
    if (tab === "auto") adminSchedulerStatus().then(setSched).catch(() => {});
  }, [tab]);

  const runOne = async (spotlight) => {
    setRunning(true);
    try {
      await adminSchedulerRunNow(spotlight);
      const [s, arts, st] = await Promise.all([
        adminSchedulerStatus(),
        adminListArticles(),
        adminStats(),
      ]);
      setSched(s);
      setArticles(arts);
      setStats(st);
    } catch (e) {
      window.alert("Generation failed. Check backend logs.");
    } finally {
      setRunning(false);
    }
  };

  const runDailyNow = async () => {
    if (!window.confirm("Generate today's full daily batch now (2 articles, +1 spotlight if Friday)? This may take 1-3 minutes.")) return;
    setRunning(true);
    try {
      await adminSchedulerRunDaily();
      const [s, arts, st] = await Promise.all([
        adminSchedulerStatus(),
        adminListArticles(),
        adminStats(),
      ]);
      setSched(s);
      setArticles(arts);
      setStats(st);
    } catch (e) {
      window.alert("Daily run failed. Check backend logs.");
    } finally {
      setRunning(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    await adminDeleteArticle(id);
    loadArticles(q);
    loadStats();
  };

  if (authLoading || !user) return null;

  return (
    <div className="min-h-screen bg-[#F4F4F0]" data-testid="admin-dashboard">
      <MetaTags title="Admin" />
      <header className="bg-[#121212] text-white border-b border-[#FF3823]">
        <div className="ceg-container py-4 flex items-center justify-between">
          <Link to="/" className="font-black tracking-tighter text-xl">
            Chicago Electronics<span className="text-[#FF3823]">.</span> <span className="text-xs uppercase tracking-widest text-white/60 ml-2">CMS</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-xs uppercase tracking-wider text-white/70 hidden md:inline" data-testid="admin-email">{user.email}</span>
            <button onClick={() => { logout(); navigate("/admin21"); }} className="text-xs uppercase tracking-wider flex items-center gap-1 hover:text-[#FF3823]" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <section className="ceg-container py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#121212] border border-[#121212] mb-8">
          {[
            { l: "Articles", v: stats?.articles ?? "-", icon: FileText },
            { l: "Featured", v: stats?.featured_articles ?? "-", icon: Star },
            { l: "Contact msgs", v: stats?.contact_submissions ?? "-", icon: Mail },
            { l: "Newsletter", v: stats?.newsletter_signups ?? "-", icon: Bell },
          ].map((s) => (
            <div key={s.l} className="bg-white p-5" data-testid={`stat-${s.l.toLowerCase().replace(/ /g,"-")}`}>
              <div className="flex items-center gap-2 ceg-overline"><s.icon size={12} /> {s.l}</div>
              <p className="font-['Outfit'] font-black text-3xl tracking-tighter mt-2">{s.v}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-px bg-[#121212] border border-[#121212] mb-6">
          {[
            { id: "articles", label: "Articles" },
            { id: "auto", label: "Auto-publish" },
            { id: "contact", label: "Contact messages" },
            { id: "newsletter", label: "Newsletter" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold ${
                tab === t.id ? "bg-[#FF3823] text-white" : "bg-white text-[#121212]"
              }`}
              data-testid={`tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "articles" && (
          <>
            <div className="flex flex-wrap gap-3 items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <input
                  value={q}
                  onChange={(e) => { setQ(e.target.value); loadArticles(e.target.value); }}
                  placeholder="Search articles..."
                  className="bg-white border border-[#121212] px-3 py-2 outline-none focus:border-[#0055FF] text-sm w-64"
                  data-testid="admin-search"
                />
                <span className="text-xs uppercase tracking-wider text-[#5C5C5C]">{articles.length} total</span>
              </div>
              <div className="flex gap-3">
                <Link to="/admin/generate" className="bg-[#FF3823] text-white px-4 py-2 text-sm font-semibold uppercase tracking-wider hover:bg-[#cc2c1a] flex items-center gap-1" data-testid="admin-ai-generate">
                  <Sparkles size={14} /> AI Generate
                </Link>
                <Link to="/admin/articles/new" className="ceg-btn-primary py-2 px-4 text-sm flex items-center gap-1" data-testid="admin-new-article">
                  <Plus size={14} /> New article
                </Link>
              </div>
            </div>

            <div className="bg-white border border-[#121212]">
              <table className="w-full text-sm">
                <thead className="bg-[#121212] text-white">
                  <tr>
                    <th className="text-left p-3 font-semibold uppercase text-xs tracking-wider">Title</th>
                    <th className="text-left p-3 font-semibold uppercase text-xs tracking-wider hidden md:table-cell">Category</th>
                    <th className="text-left p-3 font-semibold uppercase text-xs tracking-wider hidden md:table-cell">Updated</th>
                    <th className="text-right p-3 font-semibold uppercase text-xs tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.map((a) => (
                    <tr key={a.id} className="border-t border-[#E5E5E5]" data-testid={`admin-row-${a.slug}`}>
                      <td className="p-3">
                        <p className="font-semibold">{a.title}</p>
                        <p className="text-xs text-[#5C5C5C] mt-1">/{a.slug} {a.featured && <span className="ml-1 text-[#FF3823]">★ featured</span>}</p>
                      </td>
                      <td className="p-3 hidden md:table-cell text-[#5C5C5C]">{a.category}</td>
                      <td className="p-3 hidden md:table-cell text-[#5C5C5C] text-xs">{a.updated_at?.split("T")[0]}</td>
                      <td className="p-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <a href={`/article/${a.slug}`} target="_blank" rel="noreferrer" className="text-xs uppercase underline" data-testid={`view-${a.slug}`}>View</a>
                          <Link to={`/admin/articles/${a.id}/edit`} className="p-1 hover:text-[#0055FF]" data-testid={`edit-${a.slug}`}><Edit size={14} /></Link>
                          <button onClick={() => remove(a.id)} className="p-1 hover:text-[#FF3823]" data-testid={`delete-${a.slug}`}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {articles.length === 0 && (
                    <tr><td colSpan={4} className="p-6 text-center text-sm text-[#5C5C5C]">No articles.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "contact" && (
          <div className="bg-white border border-[#121212] divide-y divide-[#E5E5E5]" data-testid="contact-list">
            {contact.length === 0 && <p className="p-6 text-sm text-[#5C5C5C]">No messages yet.</p>}
            {contact.map((c) => (
              <div key={c.id} className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-['Outfit'] font-bold">{c.subject}</p>
                    <p className="text-xs text-[#5C5C5C] mt-1">{c.name} · <a href={`mailto:${c.email}`} className="underline">{c.email}</a></p>
                  </div>
                  <span className="text-xs text-[#5C5C5C]">{c.created_at?.split("T")[0]}</span>
                </div>
                <p className="mt-3 text-sm whitespace-pre-line">{c.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "newsletter" && (
          <div className="bg-white border border-[#121212]" data-testid="newsletter-list">
            <table className="w-full text-sm">
              <thead className="bg-[#121212] text-white">
                <tr>
                  <th className="text-left p-3 uppercase text-xs tracking-wider">Email</th>
                  <th className="text-left p-3 uppercase text-xs tracking-wider">Subscribed</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id} className="border-t border-[#E5E5E5]">
                    <td className="p-3">{s.email}</td>
                    <td className="p-3 text-[#5C5C5C] text-xs">{s.created_at?.split("T")[0]}</td>
                  </tr>
                ))}
                {subs.length === 0 && <tr><td colSpan={2} className="p-6 text-center text-sm text-[#5C5C5C]">No signups yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "auto" && (
          <div className="space-y-6" data-testid="auto-publish-panel">
            <div className="grid md:grid-cols-3 gap-px bg-[#121212] border border-[#121212]">
              <div className="bg-white p-5">
                <div className="ceg-overline flex items-center gap-2"><Clock size={12} /> Next scheduled run</div>
                <p className="font-['Outfit'] font-black text-2xl tracking-tighter mt-2" data-testid="next-run">
                  {sched?.next_run_at ? new Date(sched.next_run_at).toLocaleString("en-US", { timeZone: "America/Chicago", dateStyle: "medium", timeStyle: "short" }) : "—"}
                </p>
                <p className="text-xs text-[#5C5C5C] mt-1">America/Chicago · daily at {String(sched?.hour ?? "—").padStart(2, "0")}:{String(sched?.minute ?? "—").padStart(2, "0")}</p>
              </div>
              <div className="bg-white p-5">
                <div className="ceg-overline flex items-center gap-2"><Bot size={12} /> Auto-generated</div>
                <p className="font-['Outfit'] font-black text-3xl tracking-tighter mt-2">{sched?.auto_generated_total ?? 0}</p>
                <p className="text-xs text-[#5C5C5C] mt-1">Articles produced by scheduler</p>
              </div>
              <div className="bg-white p-5">
                <div className="ceg-overline flex items-center gap-2"><Star size={12} /> Friday spotlights</div>
                <p className="font-['Outfit'] font-black text-3xl tracking-tighter mt-2">{sched?.spotlight_total ?? 0}</p>
                <p className="text-xs text-[#5C5C5C] mt-1">2aelectronics.com features</p>
              </div>
            </div>

            <div className="bg-white border border-[#121212] p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="ceg-overline">How it works</p>
                  <h3 className="font-['Outfit'] font-black tracking-tighter text-2xl mt-2">Automatic SEO articles, every day.</h3>
                  <ul className="mt-3 text-sm text-[#5C5C5C] list-disc pl-5 space-y-1 max-w-2xl">
                    <li>Every day at {String(sched?.hour ?? "—").padStart(2, "0")}:{String(sched?.minute ?? "—").padStart(2, "0")} Chicago time, the system generates <strong>{sched?.daily_count_base ?? 2} articles</strong> (1100+ words each, with 3 inline images and FAQ schema).</li>
                    <li>On <strong>Fridays</strong>, an additional <strong>featured spotlight article</strong> is generated that deep-links to a relevant <strong>2aelectronics.com</strong> page. The anchor rotates weekly (brand / clean-URL / local term), uses 1-2 links max, and adds 1-2 authority outbound links (e.g. Apple Support, Wikipedia) — webspam-safe.</li>
                    <li>Topics rotate from a curated pool (no duplicates).</li>
                    <li>Articles auto-publish — review them in the <strong>Articles</strong> tab and mark any as featured to surface them on the homepage.</li>
                  </ul>
                </div>
                <div className="flex flex-col gap-2 min-w-[200px]">
                  <button onClick={() => runOne(false)} disabled={running} className="ceg-btn-primary text-sm py-2 px-4 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="run-one-now">
                    <Play size={14} /> {running ? "Working..." : "Generate 1 now"}
                  </button>
                  <button onClick={() => runOne(true)} disabled={running} className="bg-[#FF3823] text-white py-2 px-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#cc2c1a] flex items-center justify-center gap-2 disabled:opacity-60" data-testid="run-spotlight-now">
                    <Star size={14} /> Generate spotlight
                  </button>
                  <button onClick={runDailyNow} disabled={running} className="border border-[#121212] py-2 px-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#121212] hover:text-white disabled:opacity-60" data-testid="run-daily-now">
                    Run full daily batch
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#121212]">
              <div className="bg-[#121212] text-white p-3 ceg-overline" style={{ color: "white" }}>Recent runs</div>
              <table className="w-full text-sm">
                <thead className="bg-[#F0F0F0]">
                  <tr>
                    <th className="text-left p-3 text-xs uppercase tracking-wider">When</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider">Topic</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider hidden md:table-cell">Mode</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider hidden md:table-cell">Status</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider">Words</th>
                  </tr>
                </thead>
                <tbody>
                  {(sched?.recent_log || []).map((r, i) => (
                    <tr key={i} className="border-t border-[#E5E5E5]" data-testid={`log-row-${i}`}>
                      <td className="p-3 text-[#5C5C5C] text-xs">{r.ts?.replace("T", " ").split(".")[0]}</td>
                      <td className="p-3">
                        {r.slug ? (
                          <a href={`/article/${r.slug}`} target="_blank" rel="noreferrer" className="underline underline-offset-2 decoration-[#0055FF]">{r.topic}</a>
                        ) : r.topic}
                        {r.error && <p className="text-xs text-[#FF3823] mt-1">{r.error}</p>}
                      </td>
                      <td className="p-3 hidden md:table-cell">{r.spotlight ? <span className="text-[#FF3823] font-semibold text-xs uppercase">Spotlight</span> : <span className="text-xs uppercase text-[#5C5C5C]">Standard</span>}</td>
                      <td className="p-3 hidden md:table-cell">
                        <span className={`text-xs uppercase tracking-wider font-semibold ${r.status === "ok" ? "text-[#0055FF]" : "text-[#FF3823]"}`}>{r.status}</span>
                      </td>
                      <td className="p-3 text-right text-[#5C5C5C]">{r.word_count || "—"}</td>
                    </tr>
                  ))}
                  {(sched?.recent_log || []).length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-sm text-[#5C5C5C]">No runs yet. Click "Generate 1 now" to test.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
